// Lightweight, client-side duplicate detection over the already-loaded
// contacts — the web mirror of mobile/src/utils/duplicates.ts. Keep the two in
// sync so the "possible duplicates" nudge behaves identically on both surfaces.
//
// High-precision ONLY: two contacts are treated as the same person when they
// share a normalized phone number AND a first name, OR an email address.
// Requiring a name match on phones means family members sharing a home line —
// or coworkers sharing an office line — are NOT grouped. Transitive matches are
// unioned, so A↔B (phone) and B↔C (email) collapse into one cluster.

function normPhone(n) {
  const d = (n || '').replace(/\D/g, '');
  return d.length >= 7 ? d.slice(-10) : '';
}
function normEmail(e) {
  return (e || '').trim().toLowerCase();
}
function normFirstName(c) {
  return (c?.first_name || '').trim().toLowerCase();
}

export function findDuplicateClusters(contacts) {
  const parent = {};
  const find = (x) => {
    let r = x;
    while (parent[r] !== r) r = parent[r];
    while (parent[x] !== r) {
      const next = parent[x];
      parent[x] = r;
      x = next;
    }
    return r;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };

  const firstForKey = {};
  for (const c of contacts || []) {
    const id = c?.id;
    if (!id || c.source === 'self_card') continue;
    if (parent[id] === undefined) parent[id] = id;
    const fname = normFirstName(c);
    const keys = [];
    if (fname) {
      for (const p of c.phones || []) {
        const k = normPhone(p?.number);
        if (k) keys.push(`p:${k}|${fname}`);
      }
    }
    for (const e of c.emails || []) {
      const k = normEmail(e?.email);
      if (k) keys.push('e:' + k);
    }
    for (const k of keys) {
      const seen = firstForKey[k];
      if (seen && seen !== id) union(id, seen);
      else if (!seen) firstForKey[k] = id;
    }
  }

  const groups = {};
  for (const id of Object.keys(parent)) {
    const r = find(id);
    (groups[r] ||= []).push(id);
  }
  return Object.values(groups)
    .filter((g) => g.length >= 2)
    .sort((a, b) => b.length - a.length);
}
